import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { ConfigurationRegimeResponse } from '@/api/resources/configuration/cases/regime/configuration-regime.dto'
import { configurationRegimeService } from '@/api/resources/configuration/cases/regime/configuration-regime.service'
import { ConfigurationPriorityResponse } from '@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/services/configuration-priority/configuration-priority.dto'
import { configurationPriorityService } from '@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/services/configuration-priority/configuration-priority.service'
import { ConfigurationLocalAccountingCustomerRateGridBasicFormState, initialLocalAccountingCustomerRatesGridBasicFormState } from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-modal-rates-basic-form/configuration-local-accounting-billable-clients-modal-rates-grid-basic-form.model'
import { ConfigurationLocalAccountingBillableClientsModalRulesItem } from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-modal-rules/configuration-local-accounting-billable-clients-modal-rules.model'
import { ConfigurationLocalAccountingBillableCustomerBillableRateCreateRequest, ConfigurationLocalAccountingBillableCustomerBillableRatePatchInBatchRequest, ConfigurationLocalAccountingBillableCustomerBillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.dto'
import { configurationLocalAccountingBillableCustomerRatesService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.service'
import { ConfigurationCustomerResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import { configurationLocalAccountingBillableClientsHelpersGetCenters, configurationLocalAccountingBillableClientsHelpersGetInsurers } from '@/modules/configurationModule/detail/local-accounting/billable/clients/shared/configuration-local-accounting-billable-clients-helpers'
import { BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'
import { billableRateService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.service'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { StrictNullable } from '@/shared/types/app.types'
import { DateUtils } from '@/shared/util/date-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { BModal } from 'bootstrap-vue/src/components/modal'
import moment from 'moment'
import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'

@Component({
    components: {
    }
})
export default class ConfigurationLocalAccountingBillableClientsModalRatesGridBasicForm extends Vue {
    @Ref() readonly clientsModalRatesBasicForm!: BModal
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly laboratoryId!: number
    @Prop() readonly customer!: ConfigurationCustomerResponse

    @Emit()
    create(): void {
        this.hide()
    }

    @Emit()
    update(): void {
        this.hide()
    }

    state: ConfigurationLocalAccountingCustomerRateGridBasicFormState
    constructor() {
        super()
        this.state = initialLocalAccountingCustomerRatesGridBasicFormState()
    }

    get codeRate(): string {
        return this.state.selectedRate?.code ?? ''
    }

    get startDateRate(): string {
        if (this.state.selectedRate?.startDate) {
            const date = moment(this.state.selectedRate.startDate, DateUtils.datePattern).toDate()
            return DateUtils.formatDate(date, DateUtils.localeDatePattern) ?? ''
        }
        return ''
    }

    get endDateRate(): string {
        if (this.state.selectedRate?.endDate) {
            const date = moment(this.state.selectedRate.endDate, DateUtils.datePattern).toDate()
            return DateUtils.formatDate(date, DateUtils.localeDatePattern) ?? ''
        }
        return ''
    }

    get isUpdatedForm(): boolean {
        return !!this.state?.id
    }

    get modelValidated(): boolean {
        const rateExists = !!this.state.selectedRate?.id
        const hasErrors: boolean = this.$validator.errors.any() || !rateExists
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    show(rate: StrictNullable<ConfigurationLocalAccountingBillableCustomerBillableRateResponse>): void {
        this.state = initialLocalAccountingCustomerRatesGridBasicFormState()
        if (rate) {
            this.state = {
                ...this.state,
                id: rate.id,
                selectedRate: rate.rate,
                isForAutoInvoice: rate?.isForAutoInvoice,
                casePhysicalCenterForAutoAssignment: rate.casePhysicalCenterForAutoAssignment ? {
                    id: rate.casePhysicalCenterForAutoAssignment.id,
                    text: rate.casePhysicalCenterForAutoAssignment.name
                } : null,
                caseRegimeForAutoAssignment: rate.caseRegimeForAutoAssignment ? {
                    id: rate.caseRegimeForAutoAssignment?.id,
                    text: rate.caseRegimeForAutoAssignment?.name
                } : null,
                caseInsurerForAutoAssignment: rate.caseInsurerForAutoAssignment ? {
                    id: rate.caseInsurerForAutoAssignment?.id,
                    text: rate.caseInsurerForAutoAssignment?.name
                } : null,
                casePriorityForAutoAssignment: rate.casePriorityForAutoAssignment ? {
                    id: rate.casePriorityForAutoAssignment?.id,
                    text: rate.casePriorityForAutoAssignment?.name
                } : null,
                priorityForAutomaticAssignment: rate.priorityForAutomaticAssignment ?? null
            }
        }
        this.clientsModalRatesBasicForm.show()
    }

    hide(): void {
        this.clientsModalRatesBasicForm.hide()
    }

    async doAction(): Promise<void> {
        if (this.isUpdatedForm) {
            const rateConceptsUpdateOperation = await this._updateOperation()
            if (rateConceptsUpdateOperation) {
                // emits an event
                this.update()
                await this.$validator.reset()
            }
        } else {
            const rateConceptsCreateOperation = await this._createOperation()
            if (rateConceptsCreateOperation) {
                // emits an event
                this.create()
                await this.$validator.reset()
            }
        }
    }

    async getRates(): Promise<BillableRateResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }

        const response =
            await PromiseUtils.toCallSecure(
                billableRateService.paginator({}, params, this.laboratoryId))
        return response?.content || []
    }

    async getCenters(): Promise<ConfigurationLocalAccountingBillableClientsModalRulesItem[]> {
        return configurationLocalAccountingBillableClientsHelpersGetCenters()
    }

    async getRegimes(): Promise<ConfigurationLocalAccountingBillableClientsModalRulesItem[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }

        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }

        const response: StrictNullable<IResponseAgGrid<ConfigurationRegimeResponse>> = await PromiseUtils.toCallSecure(
            configurationRegimeService.paginator(filterModel, params)
        )
        return response?.content.map((item: ConfigurationRegimeResponse) => {
            return { id: item.id, text: item.name }
        }) || []
    }

    async getInsurers(): Promise<ConfigurationLocalAccountingBillableClientsModalRulesItem[]> {
        return configurationLocalAccountingBillableClientsHelpersGetInsurers()
    }

    async getPriorities(): Promise<ConfigurationLocalAccountingBillableClientsModalRulesItem[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }

        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }

        const response: StrictNullable<IResponseAgGrid<ConfigurationPriorityResponse>> = await PromiseUtils.toCallSecure(
            configurationPriorityService.paginator(filterModel, params)
        )
        return response?.content.map((item: ConfigurationPriorityResponse) => {
            return { id: item.id, text: item.name }
        }) || []
    }

    private _createOperation(): Promise<StrictNullable<ConfigurationLocalAccountingBillableCustomerBillableRateResponse>> {
        return this._addCustomerRate(
            {
                customerId: this.customer.id,
                rateId: this.state.selectedRate?.id!,
                caseInsurerForAutoAssignmentId: this.state.caseInsurerForAutoAssignment?.id,
                casePhysicalCenterForAutoAssignmentId: this.state.casePhysicalCenterForAutoAssignment?.id,
                casePriorityForAutoAssignmentId: this.state.casePriorityForAutoAssignment?.id,
                caseRegimeForAutoAssignmentId: this.state.caseRegimeForAutoAssignment?.id,
                isForAutoInvoice: this.state?.isForAutoInvoice,
                priorityForAutomaticAssignment: this.state?.priorityForAutomaticAssignment
            }
        )
    }

    private _updateOperation(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        return this._updateCustomerRate(
            {
                id: this.state.id!,
                caseInsurerForAutoAssignmentId: this.state.caseInsurerForAutoAssignment?.id ?? null,
                casePhysicalCenterForAutoAssignmentId: this.state.casePhysicalCenterForAutoAssignment?.id ?? null,
                casePriorityForAutoAssignmentId: this.state.casePriorityForAutoAssignment?.id ?? null,
                caseRegimeForAutoAssignmentId: this.state.caseRegimeForAutoAssignment?.id ?? null,
                isForAutoInvoice: this.state.isForAutoInvoice,
                priorityForAutomaticAssignment: this.state.priorityForAutomaticAssignment ?? null
            }
        )
    }

    private async _addCustomerRate(
        request: ConfigurationLocalAccountingBillableCustomerBillableRateCreateRequest
    ): Promise<StrictNullable<ConfigurationLocalAccountingBillableCustomerBillableRateResponse>> {
        if (request) {
            const response = await this.$_toCallSecure(configurationLocalAccountingBillableCustomerRatesService.create(
                this.laboratoryId,
                request
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    private async _updateCustomerRate(
        request: ConfigurationLocalAccountingBillableCustomerBillableRatePatchInBatchRequest
    ): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (request) {
            const response = await this.$_toCallSecure(configurationLocalAccountingBillableCustomerRatesService.patch(
                this.laboratoryId,
                [request]
            ))
            if (response && !PromiseUtils.isEntityIdentifierWithErrorResponse(response[0])) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }
}
